import {OrderEventType, TicketDBType, TicketType, UserType} from "data/types";
import React, {FC, useState} from "react";
import {format} from "date-fns";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcModal from "shared/NcModal/NcModal";
import ticketsTransfer from "images/tickets.png";
import {
  useExitQueueTransferTicketsMutation,
  useLazyGetCustomerByEmailQuery,
  useLazyValidatePasswordCustomerQuery,
  useTransferQueueMutation,
  useTransferTicketsMutation,
} from "store/api";
import {SuccessModal} from "components/SuccessModal/SuccessModal";
import {useNavigate} from "react-router-dom";

interface NormalTicketsProps {
  tickets: TicketDBType[];
  isSubscriber: boolean;
  order: OrderEventType;
}

const NormalTickets: FC<NormalTicketsProps> = ({
  tickets,
  isSubscriber,
  order,
}) => {
  const [selectedTickets, setSelectedTickets] = useState<TicketType[]>([]);
  const [getCustomerByEmail] = useLazyGetCustomerByEmailQuery();
  const [validatePasswordCustomer] = useLazyValidatePasswordCustomerQuery();
  const [transferQueue] = useTransferQueueMutation();
  const [transferTickets] = useTransferTicketsMutation();
  const [exitQueueTransferTickets] = useExitQueueTransferTicketsMutation();
  const [emailState, setEmailState] = useState("");
  const [passwordState, setPasswordState] = useState("");
  const [userToTransfer, setUserToTransfer] = useState<any>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const navigate = useNavigate();

  const userData = localStorage.getItem("user");
  let user: UserType;
  if (userData) {
    user = JSON.parse(userData) as UserType;
  }

  const toggleTicket = (ticket: TicketType) => {
    setSelectedTickets((prev) =>
      prev.includes(ticket)
        ? prev.filter((t) => t !== ticket)
        : [...prev, ticket]
    );
  };

  const validateEmail = async () => {
    if (emailState.toLowerCase() === user.email.toLowerCase()) {
      setAlert("No puedes transferirte tickets a ti mismo.");
      setTimeout(() => setAlert(null), 3000);
      return;
    }
    setIsLoading(true);
    const {data, error} = await getCustomerByEmail(emailState);
    if (error) {
      const fetchError = error as {
        status: number;
        data: {error: string; statusMessage: string};
      };
      setIsLoading(false);
      if (fetchError.status === 404) {
        setAlert("El correo electrónico no existe.");
        setTimeout(() => setAlert(null), 3000);
        return;
      }
    }
    if (data) {
      setIsLoading(false);
      if (data.meta.status === 200) {
        setUserToTransfer(data.customer);
      }
    }
  };

  const handleTransfer = async () => {
    setIsLoading(true);
    const {data, error} = await validatePasswordCustomer({
      customerId: user._id,
      password: passwordState,
    });
    if (error) {
      setIsLoading(false);
      const fetchError = error as {
        status: number;
        data: {error: string; statusMessage: string};
      };
      if (fetchError.status === 401) {
        setAlert("La contraseña es incorrecta.");
        setTimeout(() => setAlert(null), 3000);
        return;
      }
    }
    if (data) {
      setIsLoading(false);
      if (data.meta.status === 200) {
        setIsLoading(true);
        const {data: queueData, error: queueError} = await transferQueue({
          customer: user._id,
          type: "TRANSFER",
        });
        setIsLoading(false);
        if (queueError) {
          console.log(queueError);
          setAlert(
            "Ocurrió un error al transferir los tickets. Inténtalo de nuevo."
          );
          setTimeout(() => setAlert(null), 3000);
        }
        if (queueData) {
          console.log(queueData);
          const queueCode = queueData.cola.code;
          const queueType = queueData.cola.type;
          const ticketsToTransfer = selectedTickets.map((ticket) => ticket._id);

          const body = {
            type: "Transferencia",
            quantity: selectedTickets.length,
            customerFrom: {
              _id: user._id,
              email: user.email,
              name: user.name,
            },
            customerTo: {
              _id: userToTransfer._id,
              email: userToTransfer.email,
              name: userToTransfer.name,
            },
            orderEvent: order.orderEvent,
            tickets: ticketsToTransfer,
            eventName: order.event.nameEvent,
            idTransaction: order._id,
            codeCola: queueCode,
            typeCola: queueType,
          };
          console.log(body);

          setIsLoading(true);
          const {data: transferData, error: transferError} =
            await transferTickets(body);
          setIsLoading(false);
          if (transferError) {
            console.log(transferError);

            await exitQueueTransferTickets(queueData.cola._id);
            setIsLoading(false);
            setAlert(
              "Ocurrió un error al transferir los tickets. Inténtalo de nuevo."
            );
            setTimeout(() => setAlert(null), 3000);
            return;
          }
          if (transferData) {
            if (transferData.meta.status === 200) {
              setShowSuccessModal(true);
            }
          }
        }
      }
    }
  };

  return (
    <div>
      {!isSubscriber && (
        <div className="flex items-center justify-between mb-4 px-4 sm:px-6">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Selecciona los tickets que deseas transferir.
          </p>
          {selectedTickets.length > 0 && (
            <NcModal
              modalTitle="Transferir Tickets"
              contentExtraClass="w-full md:w-1/2"
              onCloseModal={() => {
                setUserToTransfer(null);
                setEmailState("");
              }}
              renderTrigger={(openModal) => (
                <ButtonPrimary
                  className="duration-300 animate-fadeIn"
                  onClick={() => openModal()}
                >
                  Transferir
                </ButtonPrimary>
              )}
              renderContent={() => (
                <div className="p-6 text-center flex flex-col items-center space-y-6">
                  <img className="w-40" src={ticketsTransfer} alt="transfer" />
                  {userToTransfer ? (
                    <h2 className="text-sm mb-4">
                      Se va a transferir {selectedTickets.length} ticket(s) a{" "}
                      <strong>{userToTransfer.name}</strong>
                    </h2>
                  ) : (
                    <h2 className="text-sm mb-4">
                      Ingrese el correo electrónico del usuario al que desea
                      transferir los tickets seleccionados
                    </h2>
                  )}
                  {!userToTransfer ? (
                    <div className="flex items-center space-x-4 w-full justify-center">
                      <i className="las la-envelope flex-shrink-0"></i>
                      <input
                        type="email"
                        value={emailState}
                        placeholder="Correo"
                        maxLength={30}
                        minLength={4}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmailState(value);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4 w-full justify-center">
                      <i className="las la-key flex-shrink-0"></i>
                      <input
                        type="password"
                        value={passwordState}
                        placeholder="Contraseña"
                        onChange={(e) => {
                          const value = e.target.value;
                          setPasswordState(value);
                        }}
                      />
                    </div>
                  )}
                  <ButtonPrimary
                    disabled={
                      !userToTransfer
                        ? emailState.length === 0
                        : passwordState.length === 0
                    }
                    loading={isLoading}
                    onClick={!userToTransfer ? validateEmail : handleTransfer}
                  >
                    {!userToTransfer ? "Validar email" : "Transferir"}
                  </ButtonPrimary>
                  {alert && (
                    <div className="fixed top-8 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-2xl shadow-2xl transition-all duration-300 ease-in-out opacity-90 hover:opacity-100 hover:scale-105 flex items-center gap-4">
                      {alert}
                    </div>
                  )}
                  <NcModal
                    isOpenProp={showSuccessModal}
                    modalTitle="Transferencia"
                    contentExtraClass="max-w-md"
                    triggerText=""
                    renderContent={() => (
                      <SuccessModal
                        message="La transferencia se ha realizado con éxito"
                        onClick={() => navigate(0)}
                      />
                    )}
                  />
                </div>
              )}
            />
          )}
        </div>
      )}

      <ul className="flex flex-col gap-4 px-4 sm:px-6">
        {tickets.map((ticket) => {
          const {
            _id,
            qrTicket,
            phase: {priceWithVAT},
            locality,
          } = ticket.tickets;

          const {nameEvent, img, city, startTime, place} =
            ticket.orderEvent.event;
          const {name} = ticket.customer;

          const formattedDate = startTime
            ? format(new Date(startTime), "dd-MM-yyyy")
            : "Fecha no disponible";

          const formattedTime = startTime
            ? format(new Date(startTime), "HH:mm")
            : "Hora no disponible";

          const isSelected = selectedTickets.includes(ticket.tickets);

          return (
            <li
              key={_id}
              className={`relative flex flex-col lg:flex-row rounded-3xl border overflow-hidden shadow-lg transition-transform duration-200 ease-in-out 
              ${
                isSelected
                  ? "border-primary-500 ring-2 ring-primary-300 dark:ring-primary-700 bg-primary-50 dark:bg-primary-900/40"
                  : "border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:scale-105"
              } hover:cursor-pointer`}
              onClick={() =>
                !isSubscriber ? toggleTicket(ticket.tickets) : null
              }
            >
              {/* Perforaciones a la izquierda */}
              <div className="w-4 bg-inherit flex-shrink-0 hidden lg:flex flex-col justify-center">
                <div className="w-1 h-4 bg-neutral-300 dark:bg-neutral-700 rounded-full mx-auto my-2"></div>
                <div className="w-1 h-4 bg-neutral-300 dark:bg-neutral-700 rounded-full mx-auto my-2"></div>
              </div>

              {/* Contenido principal */}
              <div className="flex flex-col sm:flex-row flex-1 items-center gap-4 p-4 relative">
                <img
                  src={img}
                  alt={nameEvent}
                  className="w-32 h-32 object-cover rounded-2xl flex-shrink-0"
                />

                <div className="flex flex-col justify-center gap-1 text-center sm:text-left">
                  <p className="text-md font-bold text-neutral-900 dark:text-white">
                    {nameEvent}
                  </p>
                  <div className="flex justify-center sm:justify-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                    <span>{city.description}</span>
                    <span>{formattedDate}</span>
                    <span>{formattedTime}</span>
                  </div>
                  <p className="text-xs text-neutral-700 dark:text-neutral-300">
                    {name}
                  </p>
                  <p className="text-xs text-neutral-700 dark:text-neutral-300">
                    {place}
                  </p>
                  <p className="text-[11px] leading-3 text-neutral-700 dark:text-neutral-300">
                    {ticket.tickets.ticketCode}
                  </p>
                  <div className="w-full flex items-center justify-between text-[11px] text-neutral-700 dark:text-neutral-300">
                    <p className="text-[11px] leading-3">
                      {ticket.tickets.numberTicket}
                    </p>

                    {ticket.tickets.number !== "0_0" && (
                      <div className="flex items-center space-x-1">
                        <i className="las la-couch text-lg flex-shrink-0"></i>
                        <p className="text-sm leading-3">
                          {ticket.tickets.number}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* QR */}
                <div className="relative flex flex-col items-center justify-center gap-2 pt-4 sm:pt-0 sm:ml-auto">
                  <div className="hidden sm:block absolute -left-5 top-0 h-full border-l-2 border-dashed border-neutral-300 dark:border-neutral-700"></div>

                  <p className="text-sm font-semibold text-center text-neutral-900 dark:text-white z-10">
                    {ticket.tickets.haveSpecialDiscount
                      ? `${locality?.description} *`
                      : locality.description}
                  </p>
                  {qrTicket && (
                    <img
                      src={qrTicket}
                      alt="QR"
                      className="w-32 h-32 object-contain z-10 rounded-lg"
                    />
                  )}
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white z-10">
                    Precio: ${priceWithVAT}
                  </p>
                </div>
              </div>

              {/* Perforaciones a la derecha */}
              <div className="w-4 bg-inherit flex-shrink-0 hidden lg:flex flex-col justify-center">
                <div className="w-1 h-4 bg-neutral-300 dark:bg-neutral-700 rounded-full mx-auto my-2"></div>
                <div className="w-1 h-4 bg-neutral-300 dark:bg-neutral-700 rounded-full mx-auto my-2"></div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NormalTickets;
